import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ActionCreators from "../../actions";
import { setLyftReportData, setTuroReportData, setOtherReportData } from "../../utils/functions";
import { getCertificateFontDownloads } from "../../utils/certificateFontDownloads";
import "./certificateDownloadButton.css";
import moment from "moment";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Image,
  Document,
  StyleSheet,
  Font,
  Svg,
  Path,
} from "@react-pdf/renderer";

import kalamFont from "../../font/Kalam-Light.ttf";
import "../../font/registerPatrickHandAndCaveatPdf";
import LatoBold from "../../font/Lato-Bold.ttf";
import LatoBlack from "../../font/Lato-Black.ttf";
import LatoLight from "../../font/Lato-Light.ttf";

Font.register({ family: "kalamFont", format: "truetype", src: kalamFont });
Font.register({ family: "LatoBold", format: "truetype", src: LatoBold });
Font.register({ family: "LatoBlack", format: "truetype", src: LatoBlack });
Font.register({ family: "LatoLight", format: "truetype", src: LatoLight });

const inspectionColumns = [
  [
    {
      number: 1,
      label: "Foot brakes",
      note: "(pads/shoes thickness)",
      aliases: ["foot brake", "foot brakes", "brakes"],
      details: [
        {
          type: "text",
          label: "Min. per manufacturer:",
          inline: [
            { label: "Front", value: "3mm" },
            { label: "Rear", value: "3mm" },
          ],
        },
        { type: "measurement", label: "Front Brake Left", value: "7mm" },
        { type: "measurement", label: "Front Brake Right", value: "7mm" },
        { type: "measurement", label: "Rear Brake Left", value: "7mm" },
        { type: "measurement", label: "Rear Brake Right", value: "7mm" },
      ],
    },
    { number: 2, label: "Emergency brake", note: "(parking brake)", aliases: ["emergency brake", "parking brake"] },
    {
      number: 3,
      label: "Steering mechanism",
      aliases: ["steering"],
      subChecks: [
        { label: "Ball joints", aliases: ["ball joint"] },
        { label: "Tie rods", aliases: ["tie rod"] },
        { label: "Rack & pinion", aliases: ["rack", "pinion"] },
        { label: "Bushings", aliases: ["bushing", "bushings"] },
      ],
    },
    {
      number: 4,
      label: "Windshield",
      aliases: ["windshield"],
      subChecks: [
        { label: "Large crack", aliases: ["large crack"] },
        { label: "Small crack", aliases: ["small crack"] },
      ],
    },
    { number: 5, label: "Rear window and other glass", aliases: ["rear window", "other glass"] },
    { number: 6, label: "Windshield wipers", aliases: ["wiper", "wipers"] },
    { number: 7, label: "Headlights", aliases: ["headlight", "headlights"] },
    { number: 8, label: "Tail lights", aliases: ["tail light", "tail lights"] },
  ],
  [
    { number: 9, label: "Turn indicator lights", aliases: ["turn indicator", "turn signal", "indicator lights"] },
    { number: 10, label: "Stop Lights", aliases: ["stop light", "brake light"] },
    { number: 11, label: "Front seat adjustment", aliases: ["front seat adjustment", "seat adjustment"] },
    { number: 12, label: "Doors", note: "(open, close, lock)", aliases: ["door", "doors"] },
    { number: 13, label: "Horn", aliases: ["horn"] },
    { number: 14, label: "Speedometer", aliases: ["speedometer"] },
    { number: 15, label: "Bumpers", aliases: ["bumper", "bumpers"] },
    { number: 16, label: "Muffler and exhaust system", aliases: ["muffler", "exhaust"] },
    {
      number: 17,
      label: "Tires, incl. tread depth",
      aliases: ["tire", "tires", "tread depth"],
      details: [
        { type: "tire", label: "Right front", category: "right_front_tire" },
        { type: "tire", label: "Left front", category: "left_front_tire" },
        { type: "tire", label: "Right rear", category: "right_rear_tire" },
        { type: "tire", label: "Left rear", category: "left_rear_tire" },
      ],
    },
    {
      number: 18,
      label: "Interior and exterior rear view mirrors",
      aliases: ["rear view mirror", "rearview mirror", "mirrors"],
    },
    {
      number: 19,
      label: "Safety belts for driver and passenger(s)",
      aliases: ["safety belt", "seat belt"],
    },
  ],
];

const formatDate = (value, format = "MM/DD/YYYY") =>
  value ? moment(value).format(format) : "";

const getFullName = (data = {}) =>
  `${data?.Username || ""} ${data?.UserlastName || ""}`.trim();

const getInspectorName = (data = {}) =>
  `${data?.Inspectorname || ""} ${data?.InspectorlastName || ""}`.trim() ||
  getFullName(data);

const getTireDimension = (reportData, category) => {
  const files = reportData?.Files || [];
  const match = files.find(
    (file) => file?.category === category || file?.category === "tire"
  );
  if (!match || match?.dimension === null || match?.dimension === undefined) {
    return "4/32";
  }
  return match.dimension;
};

const humanTickPaths = [
  "M4 12.5C6.3 15.4 8.1 17.7 9.2 18.7C12.5 13.5 16.1 8.8 21 4.4",
  "M3.5 13.2C5.9 14.9 7.6 17.1 9.5 19.2C12.2 13.2 16.6 7.7 20.6 5.2",
  "M4.2 11.8C6.7 14.1 8.3 16.6 10.1 18.1C13.4 12.2 16.7 8.1 21.4 4.8",
  "M3.9 12.9C6 15.2 7.8 16.7 9.7 18.8C12.3 13.6 15.9 7.9 20.8 5",
  "M4.1 13.4C6.5 15 8.5 17.8 9.6 18.9C12.7 12.9 16.9 8.2 21.2 4.7",
];

const handDrawnCirclePath =
  "M52.1927 2.90815C34.1317 2.90815 15.3734 8.31886 6.56927 25.4371C3.77005 30.8798 1.5 32.7484 1.5 38.8919C1.5 44.4471 5.2551 50.3534 10.1902 53.2854C14.6663 55.9447 15.5908 61.4646 20.3287 63.7676C25.118 66.0956 26.9011 68.1651 32.5593 69.1652C42.084 70.8488 57.582 73.3293 66.6763 69.3999C70.4649 67.7629 77.2718 68.2962 81.4818 67.366C84.6931 66.6565 103.112 62.3328 104.253 58.4483C106.691 50.1537 115.056 36.3085 107.23 28.2532C102.352 23.2319 97.9017 18.6987 91.2985 15.5807C86.0695 13.1115 82.0045 12.4543 76.8149 10.6525C70.3868 8.42063 63.9661 4.31621 57.262 4.31621C51.9098 4.31621 47.9811 4.76555 43.5025 1.50009";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    color: "#303030",
    fontFamily: "Helvetica",
    paddingBottom: 0,
  },
  header: {
    backgroundColor: "#211d1d",
    height: 56,
    justifyContent: "center",
    paddingLeft: 58,
  },
  brand: {
    color: "#fff",
    fontSize: 19,
    letterSpacing: 3.5,
  },
  content: {
    paddingHorizontal: 58,
    paddingTop: 14,
  },
  title: {
    fontSize: 15,
    letterSpacing: 3,
    marginBottom: 12,
  },
  inspectionGrid: {
    flexDirection: "row",
  },
  inspectionColumn: {
    flex: 1,
  },
  inspectionColumnLeft: {
    marginRight: 44,
  },
  columnHeading: {
    alignItems: "center",
    color: "#777",
    flexDirection: "row",
    fontSize: 9,
    letterSpacing: 2.5,
    marginBottom: 5,
  },
  pointHeading: {
    flex: 1,
  },
  statusHeading: {
    letterSpacing: 0,
    textAlign: "center",
    width: 40,
  },
  inspectionRow: {
    alignItems: "center",
    flexDirection: "row",
    minHeight: 24,
  },
  inspectionLabel: {
    alignItems: "baseline",
    flex: 1,
    flexDirection: "row",
  },
  number: {
    color: "#888",
    fontSize: 9.5,
    width: 16,
  },
  labelBold: {
    fontFamily: "LatoBlack",
    fontSize: 10.8,
    letterSpacing: 0.5,
    lineHeight: 1.15,
  },
  note: {
    fontFamily: "LatoBlack",
    fontSize: 10.6,
    marginLeft: 4,
  },
  subLabel: {
    fontFamily: "LatoLight",
    fontSize: 10.8,
    letterSpacing: 0.5,
    marginLeft: 16,
  },
  boxWrap: {
    alignItems: "center",
    width: 40,
  },
  box: {
    borderColor: "#777",
    borderWidth: 1,
    height: 10,
    position: "relative",
    width: 10,
  },
  humanTick: {
    height: 15,
    left: -2,
    position: "absolute",
    top: -6,
    width: 15,
  },
  detailRow: {
    alignItems: "center",
    flexDirection: "row",
    minHeight: 21,
    paddingLeft: 16,
  },
  detailLabel: {
    flex: 1,
    fontFamily: "LatoLight",
    fontSize: 10.8,
  },
  detailMid: {
    fontFamily: "LatoLight",
    fontSize: 10.8,
    marginRight: 6,
  },
  detailLine: {
    borderBottomColor: "#777",
    borderBottomWidth: 1,
    justifyContent: "flex-end",
    minHeight: 13,
    width: 54,
  },
  detailLineValue: {
    fontSize: 10,
    lineHeight: 1,
    marginBottom: 0,
    textAlign: "center",
  },
  resultBox: {
    alignItems: "center",
    borderColor: "#777",
    borderStyle: "dashed",
    borderWidth: 1.5,
    flexDirection: "row",
    marginTop: 9,
    minHeight: 68,
    paddingHorizontal: 14,
  },
  resultTitle: {
    flex: 1,
    paddingLeft: 12,
  },
  resultTitleStrong: {
    fontFamily: "LatoBold",
    fontSize: 10.5,
    letterSpacing: 1,
  },
  resultTitleSmall: {
    fontSize: 9,
    letterSpacing: 1.5,
  },
  resultOption: {
    alignItems: "center",
    height: 42,
    justifyContent: "center",
    marginLeft: 2,
    fontFamily: "LatoBold",
    fontSize: 12.8,
    position: "relative",
    width: 70,
  },
  resultOptionText: {
    fontFamily: "LatoBold",
    fontSize: 12.8,
    textAlign: "center",
  },
  resultCircle: {
    height: 42,
    left: 6,
    position: "absolute",
    top: 0,
    width: 56,
  },
  partnerGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 18,
  },
  field: {
    marginBottom: 10,
    marginRight: 20,
  },
  fieldValue: {
    borderBottomColor: "#777",
    borderBottomWidth: 1,
    fontSize: 11.5,
    lineHeight: 1,
    minHeight: 14,
    paddingBottom: 2,
  },
  fieldLabel: {
    fontSize: 8.7,
    letterSpacing: 1.3,
    marginTop: 2,
    textTransform: "uppercase",
  },
  wPartnerHalf: {
    width: 420,
  },
  wPartnerThird: {
    width: 306,
  },
  wPartnerPhone: {
    width: 272,
  },
  wPartnerDate: {
    marginRight: 0,
    width: 102,
  },
  inspectorSection: {
    backgroundColor: "#E9E9E9",
    marginTop: 0,
    marginLeft: -12,
    marginRight: -12,
    paddingBottom: 4,
    paddingHorizontal: 16,
    paddingTop: 10,
  },
  inspectorTitle: {
    fontFamily: "LatoBlack",
    fontSize: 9.3,
    marginBottom: 18,
  },
  inspectorGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  wCompany: {
    width: 250,
  },
  wWide: {
    width: 300,
  },
  wTwoColumn: {
    marginRight: 0,
    width: 442,
  },
  wSmall: {
    marginRight: 0,
    width: 122,
  },
  wFull: {
    marginRight: 0,
    width: 712,
  },
  signatureImage: {
    bottom: 2,
    height: 26,
    left: 0,
    position: "absolute",
    width: 130,
  },
  signatureWrap: {
    position: "relative",
  },
});

const Field = ({ label, value, style, handwritingStyle, children }) => (
  <View style={[styles.field, style].filter(Boolean)}>
    <Text style={[styles.fieldValue, handwritingStyle].filter(Boolean)}>
      {value || ""}
    </Text>
    <Text style={styles.fieldLabel}>{label}</Text>
  </View>
);

const SignatureField = ({ label, source, style, handwritingStyle }) => (
  <View style={[styles.field, style].filter(Boolean)}>
    <View style={styles.signatureWrap}>
      <Text style={[styles.fieldValue, handwritingStyle].filter(Boolean)}> </Text>
      {source ? <Image style={styles.signatureImage} source={source} /> : null}
    </View>
    <Text style={styles.fieldLabel}>{label}</Text>
  </View>
);

const HumanTick = ({ tickIndex = 0 }) => (
  <Svg style={styles.humanTick} viewBox="0 0 24 24">
    <Path
      d={humanTickPaths[tickIndex % humanTickPaths.length]}
      stroke="#000"
      strokeWidth={2.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </Svg>
);

const Box = ({ checked, tickIndex }) => (
  <View style={styles.boxWrap}>
    <View style={styles.box}>
      {checked ? <HumanTick tickIndex={tickIndex} /> : null}
    </View>
  </View>
);

const DetailLine = ({ value, handwritingStyle, style }) => (
  <View style={[styles.detailLine, style].filter(Boolean)}>
    <Text style={[styles.detailLineValue, handwritingStyle].filter(Boolean)}>
      {value || ""}
    </Text>
  </View>
);

const DetailRow = ({ detail, reportData, handwritingStyle }) => {
  if (detail.type === "text") {
    return (
      <View style={styles.detailRow}>
        <Text style={styles.detailLabel}>{detail.label}</Text>
        {detail.inline.map((item) => (
          <React.Fragment key={item.label}>
            <Text style={styles.detailMid}>{item.label}</Text>
            <DetailLine
              value={item.value}
              handwritingStyle={handwritingStyle}
              style={{ marginRight: 15, marginTop: -5 }}
            />
          </React.Fragment>
        ))}
      </View>
    );
  }

  if (detail.type === "tire") {
    return (
      <View style={styles.detailRow}>
        <Text style={styles.detailLabel}>{detail.label}</Text>
        <Text style={styles.detailMid}>[32nd's / In]</Text>
        <DetailLine
          value={getTireDimension(reportData, detail.category)}
          handwritingStyle={handwritingStyle}
          style={{ marginRight: 15, marginTop: -10 }}
        />
      </View>
    );
  }

  return (
    <View style={styles.detailRow}>
      <Text style={styles.detailLabel}>{detail.label}</Text>
      <Text style={[styles.detailMid, { marginTop: 2 }]}>Measurements</Text>
      <DetailLine
        value={detail.value || "7mm"}
        handwritingStyle={handwritingStyle}
        style={{ marginRight: 15, marginTop: -10 }}
      />
    </View>
  );
};

const InspectionItem = ({ item, reportData, handwritingStyle, tickIndex }) => {
  const status = "pass";
  return (
    <>
      <View style={styles.inspectionRow}>
        <View style={styles.inspectionLabel}>
          <Text style={styles.number}>{item.number}</Text>
          <Text style={styles.labelBold}>{item.label}</Text>
          {item.note ? <Text style={styles.note}>{item.note}</Text> : null}
        </View>
        <Box checked={status === "pass"} tickIndex={tickIndex} />
        <Box checked={status === "fail"} tickIndex={tickIndex + 2} />
      </View>
      {item.details?.map((detail, index) => (
        <DetailRow
          detail={detail}
          reportData={reportData} certificateData={reportData}
          handwritingStyle={handwritingStyle}
          key={`${item.number}-${index}`}
        />
      ))}
      {item.subChecks?.map((subCheck, index) => {
        const subStatus = "pass";
        return (
          <View style={styles.inspectionRow} key={subCheck.label}>
            <Text style={[styles.inspectionLabel, styles.subLabel]}>
              {subCheck.label}
            </Text>
            <Box checked={subStatus === "pass"} tickIndex={tickIndex + index + 4} />
            <Box checked={subStatus === "fail"} tickIndex={tickIndex + index + 6} />
          </View>
        );
      })}
    </>
  );
};

const ResultStatus = ({ selected, children, style }) => (
  <View style={[styles.resultOption, style].filter(Boolean)}>
    {selected ? (
      <Svg style={styles.resultCircle} viewBox="0 0 112 73">
        <Path
          d={handDrawnCirclePath}
          stroke="#000"
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
    ) : null}
    <Text style={styles.resultOptionText}>{children}</Text>
  </View>
);

function UberRasierCertificateDownLoad({
  certificateData,
  handwritingFont = "kalamFont",
  fileName = "Vehicle-certificate.pdf",
  linkLabel = "Download pdf",
}) {
  const handwritingStyle = { fontFamily: handwritingFont };
  const finalStatus = String(certificateData?.finalStatus || "").toLowerCase();
  const inspectorName = getInspectorName(certificateData);

  return (
    <div className="App">
      <PDFDownloadLink
        document={
          <Document>
            <Page size="A3" style={styles.page} wrap={false}>
              <View style={styles.header}>
                <Text style={styles.brand}>RASIER</Text>
              </View>
              <View style={styles.content}>
                <Text style={styles.title}>VEHICLE INSPECTION</Text>
                <View style={styles.inspectionGrid}>
                  {inspectionColumns.map((column, columnIndex) => (
                    <View
                      style={
                        columnIndex === 0
                          ? [styles.inspectionColumn, styles.inspectionColumnLeft]
                          : styles.inspectionColumn
                      }
                      key={columnIndex}
                    >
                      <View style={styles.columnHeading}>
                        <Text style={styles.pointHeading}>INSPECTION POINT</Text>
                        <Text style={styles.statusHeading}>PASS</Text>
                        <Text style={styles.statusHeading}>FAIL</Text>
                      </View>
                      {column.map((item, itemIndex) => (
                        <InspectionItem
                          item={item}
                          reportData={certificateData}
                          handwritingStyle={handwritingStyle}
                          tickIndex={columnIndex * 20 + itemIndex}
                          key={item.number}
                        />
                      ))}
                      {columnIndex === 1 ? (
                        <View style={styles.resultBox}>
                          <View style={styles.resultTitle}>
                            <Text style={styles.resultTitleStrong}>
                              VEHICLE INSPECTION
                            </Text>
                            <Text style={styles.resultTitleSmall}>
                              (Please circle)
                            </Text>
                          </View>
                          <ResultStatus selected={finalStatus === "pass"}>
                            PASS
                          </ResultStatus>
                          <ResultStatus
                            selected={finalStatus === "fail"}
                            style={{ marginRight: 6 }}
                          >
                            FAIL
                          </ResultStatus>
                        </View>
                      ) : null}
                    </View>
                  ))}
                </View>

                <View style={styles.partnerGrid}>
                  <Field
                    label="RASIER PARTNER NAME"
                    value={getFullName(certificateData)}
                    style={styles.wPartnerHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="EMAIL ADDRESS"
                    value={certificateData?.Useremail}
                    style={[styles.wPartnerHalf, { width: 280, marginRight: 0 }]}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="RASIER PARTNER SIGNATURE"
                    style={styles.wPartnerThird}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="RASIER PARTNER PHONE NUMBER"
                    value={certificateData?.Userphone}
                    style={styles.wPartnerPhone}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="DATE"
                    value={formatDate(certificateData?.reviewedDate)}
                    style={styles.wPartnerDate}
                    handwritingStyle={handwritingStyle}
                  />
                </View>

                <View style={styles.inspectorSection}>
                  <Text style={styles.inspectorTitle}>
                    TO BE COMPLETED BY INSPECTOR
                  </Text>
                  <View style={styles.inspectorGrid}>
                    <Field label="COMPANY" value="Chex Inc." style={styles.wCompany} handwritingStyle={handwritingStyle} />
                    <Field label="VEHICLE MILEAGE" value={certificateData?.milage} style={styles.wTwoColumn} handwritingStyle={handwritingStyle} />
                    <Field label="LICENSE PLATE #" value={certificateData?.licensePlateNumber} style={styles.wCompany} handwritingStyle={handwritingStyle} />
                    <Field label="VIN#" value={certificateData?.vin} style={styles.wTwoColumn} handwritingStyle={handwritingStyle} />
                    <Field label="VEHICLE MAKE" value={certificateData?.make} style={styles.wCompany} handwritingStyle={handwritingStyle} />
                    <Field label="VEHICLE MODEL" value={certificateData?.model} style={styles.wWide} handwritingStyle={handwritingStyle} />
                    <Field label="VEHICLE YEAR" value={certificateData?.year} style={styles.wSmall} handwritingStyle={handwritingStyle} />
                    <Field label="ADDRESS" value={certificateData?.stateAddress} style={styles.wFull} handwritingStyle={handwritingStyle} />
                    <Field label="INSPECTOR NAME" value={inspectorName} style={styles.wCompany} handwritingStyle={handwritingStyle} />
                    <SignatureField
                      label="INSPECTOR SIGNATURE"
                      source={
                        certificateData?.Inspectorsignature
                          ? `${process.env.REACT_APP_AWS_S3_LINK}/${certificateData.Inspectorsignature}?x-request=xhr`
                          : ""
                      }
                      style={styles.wWide}
                      handwritingStyle={handwritingStyle}
                    />
                    <Field label="DATE" value={formatDate(certificateData?.reviewedDate)} style={styles.wSmall} handwritingStyle={handwritingStyle} />
                  </View>
                </View>
              </View>
            </Page>
          </Document>
        }
        fileName={fileName}
      >
        {({ loading }) => (
          <>
            {loading ? (
              <div className="certificate-download-button is-loading" aria-live="polite">
                <span className="certificate-download-spinner" />
                <div>Preparing PDF</div>
              </div>
            ) : (
              <div className="certificate-download-button">
                <div>{linkLabel}</div>
                <i className="fa fa-download" />
              </div>
            )}
          </>
        )}
      </PDFDownloadLink>
    </div>
  );
}


const UberRasierCertificateDownLoadConnected = (props) => {
  const { id, companyId, reportData, checksData } = props;
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      props.getVehicleCertificate({ id, companyId }, props.setLoading, "uber");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  return (
    <div className="certificate-download-group">
      {!reportData ? (
        <div className="certificate-download-button is-loading" aria-live="polite">
          <span className="certificate-download-spinner" />
          <div>Loading certificate data</div>
        </div>
      ) : (
        getCertificateFontDownloads(reportData?.certificateFont).map((cfg) => (
          <UberRasierCertificateDownLoad
            key={cfg.handwritingFont}
            reportData={reportData} certificateData={reportData}
            checksData={checksData} checks={checksData} vehicleChecksArray={checksData} firstHalfData={checksData}
            handwritingFont={cfg.handwritingFont}
            fileName={cfg.fileName}
            linkLabel={cfg.linkLabel}
          />
        ))
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const sliceData = state && state.vehicleInstruction && state.vehicleInstruction.uberVehicleCertificate;
  return {
    reportData: sliceData,
    checksData: setOtherReportData(sliceData && sliceData.Files),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(UberRasierCertificateDownLoadConnected);
